import { useRouterState } from '@tanstack/react-router'
import { useChatbotV2 } from 'features/chatbot/contexts/ChatbotSessionContext'
import { useChatbotContext } from 'features/chatbot/hooks/useChatbotContext'
import { ConversationMessage } from 'features/chatbot/types/conversation'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { useGeckoPermissions } from 'features/permissions'
import debounce from 'lodash/debounce'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  generateChatSessionId,
  InlineFeedback,
  RolodexEntryType,
  SerializedConversationMessage,
  unixTimeNow,
  UserAction,
} from 'types'

type FeedbackRating = 'up' | 'down' | null

export interface FeedbackTarget {
  key: string
  messageId?: string
  thoughtId?: string
  messageIndex?: number
  thoughtIndex?: number
}

interface FeedbackRecord {
  target: FeedbackTarget
  feedback: InlineFeedback
}

interface ChatbotFeedbackContextProps {
  sessionId: string
  getFeedback: (key: string) => InlineFeedback | undefined
  setRating: (target: FeedbackTarget, rating: FeedbackRating) => void
  setComment: (target: FeedbackTarget, comment: string) => void
  hasFeedback: boolean
  saveNow: () => Promise<void>
  recordStopGeneration: (messageIndex: number) => void
  recordEditMessage: (
    messageIndex: number,
    originalText: string,
    newText: string
  ) => void
  recordResendMessage: (messageIndex: number, text: string) => void
}

const ChatbotFeedbackContext =
  createContext<ChatbotFeedbackContextProps | null>(null)

interface ChatbotFeedbackProviderProps {
  children: React.ReactNode
  conversation: ConversationMessage[]
  siteId?: string
  organizationId?: string
  externalEntryId?: string
  externalSessionId?: string
  sessionName?: string
}

export const ChatbotFeedbackProvider: React.FC<
  ChatbotFeedbackProviderProps
> = ({
  children,
  conversation,
  siteId,
  organizationId,
  externalEntryId,
  externalSessionId,
  sessionName,
}) => {
  const useV2 = useChatbotV2()
  const [{ sessionId: generatedSessionId, entryId: generatedEntryId }] =
    useState(generateChatSessionId)
  const sessionId = externalSessionId ?? generatedSessionId
  const v1EntryId = generatedEntryId
  const v2FeedbackEntryId = generatedEntryId
  const [feedbackMap, setFeedbackMap] = useState<Map<string, FeedbackRecord>>(
    new Map()
  )
  const [userActions, setUserActions] = useState<UserAction[]>([])
  const { rolodexPut } = useRolodexEdit()
  const { user } = useGeckoPermissions()
  const routerState = useRouterState()
  const search = routerState.location.search
  const contextInfo = useChatbotContext(search)
  const saveToRolodexRef = useRef<() => Promise<void>>()
  const feedbackMapRef = useRef(feedbackMap)
  const userActionsRef = useRef(userActions)
  const conversationRef = useRef(conversation)
  const sessionNameRef = useRef(sessionName)
  const externalEntryIdRef = useRef(externalEntryId)
  const hasSavedOnce = useRef(false)
  const initialConversationLength = useRef(conversation.length)
  const hasDirtyContent = useRef(false)

  feedbackMapRef.current = feedbackMap
  userActionsRef.current = userActions
  conversationRef.current = conversation
  sessionNameRef.current = sessionName
  externalEntryIdRef.current = externalEntryId

  if (
    conversation.length > initialConversationLength.current ||
    feedbackMap.size > 0 ||
    userActions.length > 0
  ) {
    hasDirtyContent.current = true
  }

  const hasFeedback = feedbackMap.size > 0

  const serializeV2Feedback = useCallback(() => {
    const items: Array<{
      messageId?: string
      thoughtId?: string
      messageIndex?: number
      thoughtIndex?: number
      feedback: InlineFeedback
    }> = []
    for (const [, record] of feedbackMap) {
      items.push({
        messageId: record.target.messageId,
        thoughtId: record.target.thoughtId,
        messageIndex: record.target.messageIndex,
        thoughtIndex: record.target.thoughtIndex,
        feedback: record.feedback,
      })
    }
    return items
  }, [feedbackMap])

  const saveV1ChatSession = useCallback(async () => {
    if (!user?.id || !user?.email) {
      console.warn('[ChatSession] Save skipped: user not available')
      return
    }
    if (!hasDirtyContent.current) return

    const currentConversation = conversationRef.current
    const baseChatLog = currentConversation.map(serializeMessage)
    const visibleToRawIndex = buildVisibleIndexMap(currentConversation)
    const chatLog = embedFeedbackInChatLog(
      baseChatLog,
      feedbackMapRef.current,
      visibleToRawIndex
    )

    const now = unixTimeNow()
    const entry = {
      id: v1EntryId,
      type: RolodexEntryType.CHAT_SESSION,
      data: {
        sessionId,
        userId: user.email,
        userEmail: user.email,
        name: sessionNameRef.current,
        siteId: siteId || contextInfo.siteId,
        organizationId: organizationId || contextInfo.organizationIds?.[0],
        contextInfo: { ...contextInfo },
        chatLog,
        userActions: userActionsRef.current,
        createdAt: hasSavedOnce.current ? undefined : now,
        updatedAt: now,
      },
      tags: {
        organization: organizationId || contextInfo.organizationIds?.[0],
        site: siteId || contextInfo.siteId,
        user: user.email,
      },
      index: {},
    }

    try {
      await rolodexPut([entry])
      hasSavedOnce.current = true
    } catch (error) {
      console.warn('[ChatSession] Failed to save session', v1EntryId, error)
    }
  }, [
    v1EntryId,
    sessionId,
    user,
    siteId,
    organizationId,
    contextInfo,
    rolodexPut,
  ])

  const saveV2Feedback = useCallback(async () => {
    if (!user?.id) {
      console.warn('[ChatFeedback] Save skipped: user not available')
      return
    }
    if (feedbackMap.size === 0 && userActions.length === 0) return

    const chatSessionEntryId = externalEntryIdRef.current
    if (!chatSessionEntryId) {
      return
    }

    const now = unixTimeNow()
    const entry = {
      id: v2FeedbackEntryId,
      type: RolodexEntryType.CHAT_FEEDBACK,
      data: {
        chatSessionEntryId,
        userId: user.id,
        userEmail: user.email,
        sessionName: sessionNameRef.current,
        siteId: siteId || contextInfo.siteId,
        organizationId: organizationId || contextInfo.organizationIds?.[0],
        feedback: serializeV2Feedback(),
        userActions: userActionsRef.current,
        updatedAt: now,
      },
      tags: {
        organization: organizationId || contextInfo.organizationIds?.[0],
        site: siteId || contextInfo.siteId,
        user: user.id,
        chatSession: chatSessionEntryId,
      },
      index: {},
    }

    try {
      await rolodexPut([entry])
    } catch (error) {
      console.warn('[ChatFeedback] Failed to save feedback', error)
    }
  }, [
    v2FeedbackEntryId,
    user,
    siteId,
    organizationId,
    contextInfo,
    rolodexPut,
    feedbackMap,
    userActions,
    serializeV2Feedback,
  ])

  const saveToRolodex = useCallback(async () => {
    if (useV2) await saveV2Feedback()
    else await saveV1ChatSession()
  }, [useV2, saveV1ChatSession, saveV2Feedback])

  saveToRolodexRef.current = saveToRolodex

  const debouncedSave = useMemo(
    () =>
      debounce(() => {
        saveToRolodexRef
          .current?.()
          .catch((err) => console.error('Failed to save debounced:', err))
      }, 500),
    []
  )

  const setRating = useCallback(
    (target: FeedbackTarget, rating: FeedbackRating) => {
      setFeedbackMap((prev) => {
        const next = new Map(prev)
        const existing = next.get(target.key)
        if (rating === null && existing?.feedback.comment === undefined) {
          next.delete(target.key)
        } else {
          next.set(target.key, {
            target,
            feedback: {
              rating,
              comment: existing?.feedback.comment,
              timestamp: unixTimeNow(),
            },
          })
        }
        return next
      })
      debouncedSave()
    },
    [debouncedSave]
  )

  const setComment = useCallback(
    (target: FeedbackTarget, comment: string) => {
      setFeedbackMap((prev) => {
        const next = new Map(prev)
        const existing = next.get(target.key)
        if (comment === '' && existing?.feedback.rating === null) {
          next.delete(target.key)
        } else {
          next.set(target.key, {
            target,
            feedback: {
              rating: existing?.feedback.rating ?? null,
              comment: comment || undefined,
              timestamp: unixTimeNow(),
            },
          })
        }
        return next
      })
      debouncedSave()
    },
    [debouncedSave]
  )

  const getFeedback = useCallback(
    (key: string) => feedbackMap.get(key)?.feedback,
    [feedbackMap]
  )

  const saveNow = useCallback(async () => {
    debouncedSave.cancel()
    await saveToRolodexRef.current?.()
  }, [debouncedSave])

  const recordStopGeneration = useCallback(
    (messageIndex: number) => {
      setUserActions((prev) => [
        ...prev,
        {
          type: 'stop_generation',
          timestamp: unixTimeNow(),
          messageIndex,
        },
      ])
      debouncedSave()
    },
    [debouncedSave]
  )

  const recordEditMessage = useCallback(
    (messageIndex: number, originalText: string, newText: string) => {
      setUserActions((prev) => [
        ...prev,
        {
          type: 'edit_message',
          timestamp: unixTimeNow(),
          messageIndex,
          originalText,
          newText,
        },
      ])
      debouncedSave()
    },
    [debouncedSave]
  )

  const recordResendMessage = useCallback(
    (messageIndex: number, text: string) => {
      setUserActions((prev) => [
        ...prev,
        {
          type: 'resend_message',
          timestamp: unixTimeNow(),
          messageIndex,
          originalText: text,
        },
      ])
      debouncedSave()
    },
    [debouncedSave]
  )

  useEffect(() => {
    if (useV2) return
    if (conversation.length > 0 || feedbackMap.size > 0) {
      debouncedSave()
    }
  }, [conversation, feedbackMap, debouncedSave, useV2])

  useEffect(() => {
    if (
      useV2 &&
      externalEntryId &&
      (feedbackMap.size > 0 || userActions.length > 0)
    ) {
      debouncedSave()
    }
  }, [useV2, externalEntryId, feedbackMap, userActions, debouncedSave])

  useEffect(() => {
    const handleBeforeUnload = () => debouncedSave.flush()
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [debouncedSave])

  useEffect(() => {
    return () => {
      debouncedSave.flush()
    }
  }, [debouncedSave])

  const value = useMemo<ChatbotFeedbackContextProps>(
    () => ({
      sessionId,
      getFeedback,
      setRating,
      setComment,
      hasFeedback,
      saveNow,
      recordStopGeneration,
      recordEditMessage,
      recordResendMessage,
    }),
    [
      sessionId,
      getFeedback,
      setRating,
      setComment,
      hasFeedback,
      saveNow,
      recordStopGeneration,
      recordEditMessage,
      recordResendMessage,
    ]
  )

  return (
    <ChatbotFeedbackContext.Provider value={value}>
      {children}
    </ChatbotFeedbackContext.Provider>
  )
}

export function useChatbotFeedback(): ChatbotFeedbackContextProps {
  const context = useContext(ChatbotFeedbackContext)
  if (!context) {
    throw new Error(
      'useChatbotFeedback must be used within a ChatbotFeedbackProvider'
    )
  }
  return context
}

export function useChatbotFeedbackOptional(): ChatbotFeedbackContextProps | null {
  return useContext(ChatbotFeedbackContext)
}

export function buildMessageFeedbackKey(
  messageId: string | undefined,
  messageIndex: number
): string {
  return messageId ?? `msg-idx-${messageIndex}`
}

export function buildThoughtFeedbackKey(
  thoughtId: string | undefined,
  messageId: string | undefined,
  messageIndex: number,
  thoughtIndex: number
): string {
  if (thoughtId) return thoughtId
  const parent = messageId ?? `msg-idx-${messageIndex}`
  return `${parent}:thought-idx-${thoughtIndex}`
}

function serializeMessage(
  message: ConversationMessage
): SerializedConversationMessage {
  switch (message.type) {
    case 'thought':
    case 'error':
    case 'userMessage':
      return {
        type: message.type,
        text: message.text,
      }
    case 'aiResponse': {
      const responseText = message.naturalLanguageResponseBySegments
        ?.map((seg) => seg.text)
        .join('')
      return {
        type: 'aiResponse',
        naturalLanguageResponse: responseText,
        sources: message.sources,
        thinkingTimeSeconds: message.thinkingTimeSeconds,
        thinkingNotes: message.thinkingNotes?.map((note) => ({
          text: note.text,
          sources: note.sources,
        })),
        instrumentsWithData: message.instrumentsWithData,
      }
    }
  }
}

function buildVisibleIndexMap(
  conversation: ConversationMessage[]
): Map<number, number> {
  const map = new Map<number, number>()
  let visibleIndex = 0
  for (let i = 0; i < conversation.length; i++) {
    if (conversation[i].type !== 'thought') {
      map.set(visibleIndex, i)
      visibleIndex++
    }
  }
  return map
}

function embedFeedbackInChatLog(
  chatLog: SerializedConversationMessage[],
  feedbackMap: Map<string, FeedbackRecord>,
  visibleToRawIndex: Map<number, number>
): SerializedConversationMessage[] {
  if (feedbackMap.size === 0) return chatLog

  const result = chatLog.map((msg) => ({ ...msg }))

  for (const [, record] of feedbackMap) {
    const { target, feedback } = record
    const visibleIndex = target.messageIndex
    if (visibleIndex === undefined) continue
    const rawIndex = visibleToRawIndex.get(visibleIndex)
    if (rawIndex === undefined || rawIndex >= result.length) continue

    if (target.thoughtIndex === undefined) {
      result[rawIndex] = { ...result[rawIndex], feedback }
    } else {
      const msg = result[rawIndex]
      if (msg.type !== 'aiResponse' || !msg.thinkingNotes) continue
      const notes = msg.thinkingNotes.map((note) => ({ ...note }))
      const ti = target.thoughtIndex
      if (ti >= 0 && ti < notes.length) {
        notes[ti] = { ...notes[ti], feedback }
      }
      result[rawIndex] = { ...msg, thinkingNotes: notes }
    }
  }

  return result
}
